import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

import {
  FILTERABLE_GROUP_NOUN,
  filterForGroup,
  type ReportFilterChanges,
} from '../lib/report-filters';
import type { ReportGrouping } from '../types/report.types';

/**
 * A value in a report row (a brand, provider, channel or denomination) that
 * narrows the whole report to itself when clicked. Without `onApply` it is
 * plain text.
 */
export function FilterValue({
  children,
  description,
  onApply,
  className,
}: {
  children: ReactNode;
  /** What the filter keeps, e.g. "brand Netflix" — read as "Show only brand Netflix". */
  description: string;
  onApply?: () => void;
  className?: string;
}) {
  if (!onApply) return <span className={className}>{children}</span>;
  return (
    <button
      type="button"
      onClick={onApply}
      aria-label={`Show only ${description}`}
      title={`Show only ${description}`}
      className={cn(
        'hover:text-primary rounded-sm text-start underline-offset-2 hover:underline',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * A grouped table's row label: a filter button when the report is grouped by
 * channel, provider, brand or denomination (and the group is a known one).
 */
export function GroupLabel({
  grouping,
  rowKey,
  label,
  onFilter,
}: {
  grouping: ReportGrouping;
  rowKey: string;
  label: string;
  onFilter?: (changes: ReportFilterChanges) => void;
}) {
  const changes = onFilter ? filterForGroup(grouping, rowKey) : null;
  if (!onFilter || !changes) return <>{label}</>;
  return (
    <FilterValue
      description={`${FILTERABLE_GROUP_NOUN[grouping] ?? grouping} ${label}`}
      onApply={() => onFilter(changes)}
    >
      {label}
    </FilterValue>
  );
}
